import { Controller, Control, FieldErrors, UseFormHandleSubmit } from 'react-hook-form';
import { TextField, Button, FormControl, Typography, Alert } from '@mui/material';
import { useState, useEffect } from 'react';

export interface Step2FormTranslations {
  registrationComplete: string;
  createAccountMessage: string;
  email: string;
  password: string;
  confirmPassword: string;
  error: string;
  createAccount: string;
  resendEmail: string;
  resendEmailWithTimer: string;
  [key: string]: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFormControl = Control<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFieldErrors = FieldErrors<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyHandleSubmit = UseFormHandleSubmit<any>;

export interface Step2FormProps {
  control: AnyFormControl;
  errors: AnyFieldErrors;
  handleSubmit: AnyHandleSubmit;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit: (data: any) => void;
  isLoading: boolean;
  isEmailSent: boolean;
  sendEmailVerification: () => void;
  error?: string;
  t: Step2FormTranslations;
}

const Step2Form: React.FC<Step2FormProps> = ({
  control,
  errors,
  handleSubmit,
  onSubmit,
  isLoading,
  isEmailSent,
  sendEmailVerification,
  error,
  t
}) => {
  const [countdown, setCountdown] = useState<number>(60);
  const [canResend, setCanResend] = useState<boolean>(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (isEmailSent && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(prevCount => prevCount - 1);
      }, 1000);
    } else if (countdown === 0) {
      setCanResend(true);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isEmailSent, countdown]);

  const handleResendEmail = (): void => {
    sendEmailVerification();
    setCountdown(60);
    setCanResend(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Alert severity="info" sx={{ mb: 2 }}>
        {isEmailSent
          ? t.registrationComplete
          : t.createAccountMessage}
      </Alert>

      <FormControl fullWidth margin="normal">
        <Controller
          name="email"
          control={control}
          rules={{
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Please enter a valid email address'
            }
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label={t.email}
              type="email"
              required
              error={!!errors.email}
              helperText={errors.email ? t.error + errors.email.message : ''}
              autoComplete="email"
              disabled={isEmailSent}
            />
          )}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <Controller
          name="password"
          control={control}
          rules={{
            required: 'Password is required',
            minLength: {
              value: 6,
              message: 'Password must be at least 6 characters'
            }
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label={t.password}
              type="password"
              required
              error={!!errors.password}
              helperText={errors.password ? t.error + errors.password.message : ''}
              autoComplete="new-password"
              disabled={isEmailSent}
            />
          )}
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <Controller
          name="confirmPassword"
          control={control}
          rules={{
            required: 'Confirm Password is required',
            validate: (value, formValues) => value === formValues.password || 'Passwords do not match'
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label={t.confirmPassword}
              type="password"
              required
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword ? t.error + errors.confirmPassword.message : ''}
              autoComplete="new-password"
              disabled={isEmailSent}
            />
          )}
        />
      </FormControl>

      {!isEmailSent ? (
        <Button
          variant="contained"
          onClick={sendEmailVerification}
          disabled={isLoading}
          fullWidth
          sx={{ mt: 2 }}
        >
          {t.createAccount}
        </Button>
      ) : (
        <>
          <Button
            variant="outlined"
            onClick={handleResendEmail}
            disabled={!canResend || isLoading}
            fullWidth
            sx={{ mt: 2 }}
          >
            {canResend ? t.resendEmail : t.resendEmailWithTimer.replace('%s', String(countdown))}
          </Button>
        </>
      )}

      {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
    </form>
  );
};

export default Step2Form;
